/**
 * "Open now" extended with how much longer — #284.
 *
 * The backend parses a closing time out of each location's freeform hours string
 * and exposes it as facts (`closes_at`, `minutes_until_close`, `reopens_at`)
 * rather than a verdict, so each surface can render it against its own idea of
 * "long enough" — a to-go grab wants 15 minutes, settling in to work wants three
 * hours.
 *
 * ⚠️ IT EXISTS TO STOP TWO STATES COLLAPSING. `minutes_until_close == null` means
 * THREE different things depending on the open-now verdict:
 *
 *     is_open_now === null   → hours unknown       → say nothing
 *     is_open_now === false  → shut right now      → maybe "Opens 5 PM"
 *     is_open_now === true   → open, never closes  → "Open 24 hours"
 *
 * A component that checks `minutes_until_close == null` on its own renders a
 * 24-hour diner as "hours unknown". Deriving it once, here, makes that
 * unspellable at the call site — the same reason ownership.js exists for #158,
 * and the same drift #186 caused when the detail page re-derived open-now and
 * disagreed with the list card.
 *
 * Returns null when there is nothing to say, so callers can render on a truthy
 * result without repeating the state logic.
 */

/** Threshold where phrasing flips from an absolute time to a countdown. */
const COUNTDOWN_BELOW_MINUTES = 60;
/** Threshold where the remaining time is worth a colour that draws the eye. */
const URGENT_AT_OR_BELOW_MINUTES = 30;

/**
 * Render an API "HH:MM" as a locale-appropriate wall-clock time
 * ("22:00" → "10 PM", or "22:00" where the locale uses a 24-hour clock).
 *
 * ⚠️ The API string is already local to the RESTAURANT — the backend resolves
 * each location's own IANA zone (#39/#254). It is a wall-clock READING, not an
 * instant. So this builds the date in UTC and formats it in UTC, guaranteeing the
 * viewer's own timezone can never shift it. Constructing `new Date()` from it
 * would make a 10 PM close in Austin read as 8 PM to a browser in Los Angeles.
 */
export function clockText(hhmm) {
  if (typeof hhmm !== 'string') return null;
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!match) return null;

  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;

  const date = new Date(Date.UTC(2000, 0, 1, hour, minute));
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      // Drop ":00" on the hour so it reads "9 PM", not "9:00 PM".
      ...(minute === 0 ? {} : { minute: '2-digit' }),
      timeZone: 'UTC',
    }).format(date);
  } catch (error) {
    return hhmm;
  }
}

/**
 * Render a backend weekday index (Mon=0 .. Sun=6, #338) as a short local name.
 *
 * ⚠️ The index is sent rather than derived because the day offset counts from
 * the RESTAURANT's local today (#39) — a browser in another timezone cannot work
 * out which day "+2" lands on. 2001-01-01 was a Monday, so the index maps
 * straight onto a UTC date and is formatted in UTC for the same reason
 * clockText is: the viewer's zone must not be able to shift it.
 */
export function weekdayText(index) {
  if (!Number.isInteger(index) || index < 0 || index > 6) return null;
  const date = new Date(Date.UTC(2001, 0, 1 + index));
  try {
    return new Intl.DateTimeFormat(undefined, { weekday: 'short', timeZone: 'UTC' }).format(date);
  } catch (error) {
    return null;
  }
}

/**
 * When a shut place next opens, as text ready to follow "Opens ".
 *
 * 🔑 TWO FIELDS, TWO MEANINGS, and the older one is the narrower one.
 * `reopens_at` (#284) means "later TODAY" and nothing else — it is null after
 * the last close of the day and on a day the place is shut, which is most of
 * when this question gets asked. `next_open_*` (#338) searches the whole week.
 *
 * Falling back to `reopens_at` is what keeps the page correct against a backend
 * that predates #338: it is read as today, because today is all it ever meant.
 *
 * Returns null rather than guessing whenever the day cannot be named — "Opens
 * 9 AM" about next Friday is precisely the plausible-but-wrong answer #338 set
 * out to avoid.
 */
function nextOpening(source) {
  const at = clockText(source.next_open_at ?? null);
  const offset = source.next_open_day_offset;

  if (at && Number.isInteger(offset)) {
    if (offset === 0) return { dayOffset: 0, text: at };
    if (offset === 1) return { dayOffset: 1, text: `tomorrow ${at}` };
    const weekday = weekdayText(source.next_open_weekday ?? null);
    if (!weekday) return null;
    return { dayOffset: offset, text: `${weekday} ${at}` };
  }

  const todayOnly = clockText(source.reopens_at ?? null);
  return todayOnly ? { dayOffset: 0, text: todayOnly } : null;
}

/**
 * Derive the display state from a restaurant or location object.
 *
 * Works on a location (`currently_open`) or a restaurant (`is_open_now`). Since
 * #338 the restaurant roll-up carries the next opening too, which is what lets a
 * CARD — which is handed a restaurant, not a location — answer "when does it
 * open?" at all. Returns null for unknown hours, and for a closed place whose
 * next opening cannot be named.
 */
export function openStatus(source) {
  if (!source) return null;

  // Restaurants roll up as `is_open_now`; locations answer as `currently_open`.
  const isOpen = source.is_open_now ?? source.currently_open ?? null;
  const closesAt = source.closes_at ?? null;
  const minutes = source.minutes_until_close ?? null;
  const reopensAt = source.reopens_at ?? null;

  // Unknown hours. Never render as "closed" — that is the #166/#247 failure mode
  // where a place reads as *not there* rather than as unanswered.
  if (isOpen === null || isOpen === undefined) return null;

  if (isOpen !== true) {
    // #338: this used to read `reopens_at` alone, so a place shut for the day —
    // or shut all day — got no badge at all. That is the state the card is most
    // often asked about.
    const opens = nextOpening(source);
    if (!opens) return null; // shut with no knowable opening → no badge, as before
    return {
      key: opens.dayOffset === 0 ? 'closedReopens' : 'closedOpensLater',
      open: false,
      label: `Opens ${opens.text}`,
      detail: `Closed now · opens ${opens.text}`,
      variant: 'secondary',
      urgent: false,
      minutesUntilClose: null,
    };
  }

  // Open with no closing time == 24 hours. Reachable only after is_open_now has
  // been checked, which is exactly what keeps it distinct from unknown hours.
  if (closesAt === null || minutes === null) {
    return {
      key: 'open24Hours',
      open: true,
      label: 'Open 24 hours',
      detail: 'Open 24 hours',
      variant: 'success',
      urgent: false,
      minutesUntilClose: null,
    };
  }

  const until = clockText(closesAt);
  const soon = minutes < COUNTDOWN_BELOW_MINUTES;
  const urgent = minutes <= URGENT_AT_OR_BELOW_MINUTES;
  // Under an hour the countdown is what changes the decision; above it, the
  // absolute time is what you actually plan against.
  const head = soon ? `Closes in ${minutes}m` : `Open until ${until ?? '—'}`;
  const back = clockText(reopensAt);

  return {
    key: 'openUntil',
    open: true,
    label: head,
    // The split lunch/dinner tail. Without it, 1:45 PM at such a place reads as
    // "Closes in 15m" and looks like the day is over.
    detail: back ? `${head} · reopens ${back}` : head,
    variant: urgent ? 'warning' : 'success',
    urgent,
    minutesUntilClose: minutes,
  };
}

// --- #334: the open-hours filter is a DURATION, not a boolean --------------

/**
 * Whether a restaurant is still open at least `minutes` from now.
 *
 * 🔑 THIS IS A FUNCTION AND NOT AN INLINE COMPARISON for one reason:
 * `minutes_until_close == null` covers THREE states and two of them belong on
 * OPPOSITE sides of the filter.
 *
 *     24-hour place  → null minutes → MUST PASS every threshold
 *     unknown hours  → null minutes → must fail all of them
 *     closed now     → null minutes → must fail all of them
 *
 * `(r.minutes_until_close ?? 0) >= 120` silently drops every 24-hour place from
 * "open 2+ hrs" — the category that most obviously satisfies it. `?? Infinity`
 * inverts the bug and fills the list with places whose hours we cannot parse.
 * Neither default is safe, because the null is not one thing.
 *
 * `minutes === 0` reproduces the pre-#334 "open now" set exactly.
 */
export function staysOpenAtLeast(source, minutes) {
  const status = openStatus(source);
  if (!status || !status.open) return false;           // closed, or unknown → out
  if (status.minutesUntilClose === null) return true;  // 24 hours → no deadline
  return status.minutesUntilClose >= minutes;
}

/**
 * Open now, or confirmed closed and opening within `minutes` (#402). It answers
 * "will it be open by the time I get there?", so places open now count whatever
 * their closing time.
 *
 * Unknown hours stay out, the same as staysOpenAtLeast. So does a permanently
 * closed place: the backend never gives it a `minutes_until_open`. Before the
 * backend ships the field, it's undefined and only places open now match, which
 * is a safe fallback.
 */
export function opensWithin(source, minutes) {
  if (staysOpenAtLeast(source, 0)) return true;
  const status = openStatus(source);
  // `openStatus` is null for unknown hours AND for a closed place with no
  // knowable opening; either way there's nothing to promise.
  if (!status || status.open) return false;
  const until = source.minutes_until_open;
  return Number.isInteger(until) && until >= 0 && until <= minutes;
}

/**
 * The list header's open-hours options (#334). `minimumMinutes` of null means
 * "don't filter"; 0 means "open at all". The #402 entries set
 * `opensWithinMinutes` instead. Use matchesOpenFilter / isOpenFilterActive
 * rather than reading either field at the call site.
 */
export const OPEN_FILTERS = [
  { key: 'off',        minimumMinutes: null, menuLabel: 'Any time',                 chipLabel: 'Open now',    emptyStateFragment: 'open right now' },
  { key: 'openNow',    minimumMinutes: 0,    menuLabel: 'Open now',                 chipLabel: 'Open now',    emptyStateFragment: 'open right now' },
  { key: 'oneHour',    minimumMinutes: 60,   menuLabel: 'Open for another hour',    chipLabel: 'Open 1+ hr',  emptyStateFragment: 'open for another hour' },
  { key: 'twoHours',   minimumMinutes: 120,  menuLabel: 'Open for another 2 hours', chipLabel: 'Open 2+ hrs', emptyStateFragment: 'open for another 2 hours' },
  { key: 'threeHours', minimumMinutes: 180,  menuLabel: 'Open for another 3 hours', chipLabel: 'Open 3+ hrs', emptyStateFragment: 'open for another 3 hours' },
  // #402: open now, plus closed places opening soon. dividerBefore separates the
  // "stays open" group from the "opening" group in the menu.
  { key: 'opensWithinHour',     minimumMinutes: null, opensWithinMinutes: 60,  dividerBefore: true, menuLabel: 'Open or opening within the hour',    chipLabel: 'Opening ≤1 hr',  emptyStateFragment: 'open or opening within the hour' },
  { key: 'opensWithinTwoHours', minimumMinutes: null, opensWithinMinutes: 120,                      menuLabel: 'Open or opening within 2 hours',     chipLabel: 'Opening ≤2 hrs', emptyStateFragment: 'open or opening within 2 hours' },
];

/** Whether a filter narrows the list at all ('off' is the only one that doesn't). */
export function isOpenFilterActive(filter) {
  return filter.minimumMinutes !== null || Number.isInteger(filter.opensWithinMinutes);
}

/** Whether `source` passes `filter`. The one place the two filter kinds diverge. */
export function matchesOpenFilter(source, filter) {
  if (Number.isInteger(filter.opensWithinMinutes)) return opensWithin(source, filter.opensWithinMinutes);
  if (filter.minimumMinutes === null) return true;
  return staysOpenAtLeast(source, filter.minimumMinutes);
}

export function openFilterByKey(key) {
  return OPEN_FILTERS.find((f) => f.key === key) ?? OPEN_FILTERS[0];
}
